/**
 * Types and interfaces for WESTEER Event Bus.
 * @module EventBus
 */

/**
 * Function returned by event subscription methods.
 * Call this function to unsubscribe from the event.
 */
export type UnsubscribeFn = () => void;

/**
 * Generic event callback function.
 * @template T - Type of the event payload
 */
export type EventCallback<T = any> = (payload: T) => void | Promise<void>;

/**
 * Event Bus centralizado para comunicación entre módulos.
 * Permite desacoplar completamente los módulos siguiendo el principio:
 * "Módulos NO se conocen entre sí, solo conocen al Core"
 */
export interface IEventBus {
  /**
   * Escucha un evento.
   * @param event - Nombre del evento (convención: 'modulo.entidad.accion')
   * @param callback - Función a ejecutar cuando ocurra el evento
   * @returns Función para desuscribirse
   * @example
   * const unsubscribe = eventBus.on('mobility.stop.created', (payload) => {
   *   console.log('New stop:', payload.stop);
   * });
   */
  on<T = any>(event: string, callback: EventCallback<T>): UnsubscribeFn;

  /**
   * Emite un evento.
   * @param event - Nombre del evento
   * @param payload - Datos del evento (debe ser serializable)
   * @example
   * eventBus.emit('mobility.stop.created', {
   *   stopId: '123',
   *   stop: { id: '123', name: 'Main St' }
   * });
   */
  emit<T = any>(event: string, payload: T): void;

  /**
   * Remueve un listener de un evento.
   * @param event - Nombre del evento
   * @param callback - Función a remover
   */
  off<T = any>(event: string, callback: EventCallback<T>): void;

  /**
   * Escucha un evento una sola vez.
   * El listener se desuscribe automáticamente después de la primera ejecución.
   * @param event - Nombre del evento
   * @param callback - Función a ejecutar
   * @returns Función para desuscribirse (si se necesita antes de la primera ejecución)
   * @example
   * eventBus.once('core.map.loaded', () => {
   *   console.log('Map loaded - this will only log once');
   * });
   */
  once<T = any>(event: string, callback: EventCallback<T>): UnsubscribeFn;
}

/**
 * Core events emitidos por WESTEER Core.
 * Estos eventos están garantizados y documentados.
 */
export const CoreEvents = {
  // Authentication
  AUTH_LOGIN: 'core.auth.login',
  AUTH_LOGOUT: 'core.auth.logout',

  // Projects
  PROJECT_CREATED: 'core.project.created',
  PROJECT_CHANGED: 'core.project.changed',
  PROJECT_DELETED: 'core.project.deleted',

  // Scenarios
  SCENARIO_CREATED: 'core.scenario.created',
  SCENARIO_CHANGED: 'core.scenario.changed',
  SCENARIO_PUBLISHED: 'core.scenario.published',
  SCENARIO_DELETED: 'core.scenario.deleted',

  // Map
  MAP_LOADED: 'core.map.loaded',
  MAP_CLICK: 'core.map.click',
  MAP_RIGHTCLICK: 'core.map.rightclick',
  MAP_MOVE: 'core.map.move',
  MAP_FEATURE_SELECTED: 'core.map.feature.selected',
  MAP_FEATURE_DESELECTED: 'core.map.feature.deselected',
  MAP_AREA_SELECTED: 'core.map.area.selected',
  MAP_AREA_DESELECTED: 'core.map.area.deselected',
} as const;

/**
 * Payload types for core events
 */

/**
 * Payload para evento MAP_FEATURE_SELECTED
 */
export interface MapFeatureSelectedPayload {
  /**
   * Feature GeoJSON seleccionado
   */
  feature: any;

  /**
   * ID de la capa donde está el feature
   */
  layerId?: string;

  /**
   * Coordenadas del clic (lng, lat)
   */
  lngLat: { lng: number; lat: number };

  /**
   * Tipo de módulo al que pertenece el feature (opcional)
   */
  moduleId?: string;
}

/**
 * Payload para evento MAP_AREA_SELECTED
 */
export interface MapAreaSelectedPayload {
  /**
   * Bounds del área seleccionada
   */
  bounds: {
    minLng: number;
    minLat: number;
    maxLng: number;
    maxLat: number;
  };

  /**
   * GeoJSON del polígono seleccionado
   */
  geometry: any;

  /**
   * Superficie en km²
   */
  area?: number;
}
